import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Plus, Mail, Users, CheckCircle2, Trash2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import useMessageStream from '../hooks/useMessageStream';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyInvites = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upcomingBookings, setUpcomingBookings] = useState(null);    // null = loading
  const navigate = useNavigate();

  const fetchList = async () => {
    setLoading(true);
    try {
      const [res, bk] = await Promise.all([
        axios.get(`${API_URL}/api/customer/invitations`, { withCredentials: true }),
        axios.get(`${API_URL}/api/customer/upcoming-bookings`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setList(res.data || []);
      setUpcomingBookings(bk.data || []);
    } catch (e) {
      if (e.response?.status === 401) navigate('/login?next=/my-invites');
      else toast.error('Could not load invitations');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchList(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Live sync: if admin creates / deletes an invitation on behalf of this
  // customer or updates their bookings, refetch.
  useMessageStream((evt) => {
    if (!evt) return;
    if (evt.type === 'customer_updated' || evt.type === 'booking_updated') fetchList();
  });

  const remove = async (id) => {
    if (!window.confirm('Delete this invitation? All recipients will also be removed.')) return;
    try {
      await axios.delete(`${API_URL}/api/customer/invitations/${id}`, { withCredentials: true });
      toast.success('Invitation deleted');
      fetchList();
    } catch { toast.error('Could not delete'); }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF8]" data-testid="my-invites-page">
      <Header />
      <div className="container mx-auto px-4 py-10 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }} data-testid="my-invites-title">
              My Birthday Invites
            </h1>
            <p className="text-[#6B6B7A] text-sm mt-1">Create custom invites and track who's coming.</p>
          </div>
          <Link to="/my-invites/new" data-testid="create-invite-link" aria-disabled={upcomingBookings !== null && upcomingBookings.length === 0}>
            <Button
              className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white px-5 h-11"
              disabled={upcomingBookings !== null && upcomingBookings.length === 0}
              data-testid="create-invite-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Create invite
            </Button>
          </Link>
        </div>

        {/* Iter-90: When the customer has no upcoming parties, surface the
            "book a party first" message at the top so the empty-state /
            existing-invitation list still makes sense. */}
        {upcomingBookings !== null && upcomingBookings.length === 0 && (
          <div className="mb-6 rounded-2xl bg-[#FFE9EE] border-2 border-[#FF3B6B]/20 p-5 text-center" data-testid="invites-no-upcoming-bookings">
            <p className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
              You have no upcoming parties.
            </p>
            <p className="text-sm text-[#6B6B7A] mt-1">Please book a party before sending invitations.</p>
            <Button
              onClick={() => navigate('/book')}
              className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white mt-3 h-10 px-5"
              data-testid="invites-book-cta"
            >
              Book a party →
            </Button>
          </div>
        )}

        {loading ? (
          <p className="text-[#6B6B7A]">Loading…</p>
        ) : list.length === 0 ? (
          <div className="bg-white rounded-3xl border border-[#E8E5DF] p-10 text-center" data-testid="invites-empty">
            <div className="text-5xl mb-2">✉️</div>
            <h3 className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>No invitations yet</h3>
            <p className="text-sm text-[#6B6B7A] mt-1 mb-4">Send beautiful party invites to friends in minutes.</p>
            <Link to="/my-invites/new">
              <Button className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white px-5 h-11">Create your first invite</Button>
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {list.map((inv) => (
              <motion.div
                key={inv.invitation_id}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-[#E8E5DF] rounded-2xl p-4 hover:shadow-md transition-shadow"
                data-testid={`invite-card-${inv.invitation_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <Link to={`/my-invites/${inv.invitation_id}`} className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1E1E24] truncate" style={{ fontFamily: 'Fredoka' }}>
                      {inv.child_name}'s {inv.age}{['st','nd','rd'][((inv.age||0)%10)-1] && (((inv.age||0)%100<11||(inv.age||0)%100>13))? ['st','nd','rd'][((inv.age||0)%10)-1] : 'th'} Birthday
                    </p>
                    <p className="text-xs text-[#6B6B7A] mt-0.5 truncate">📅 {inv.party_date} · {inv.party_time} · {inv.location}</p>
                    <div className="mt-3 flex items-center gap-3 text-xs">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full ${inv.status==='sent'?'bg-[#06D6A0]/15 text-[#058B69]':'bg-[#FFE4EB] text-[#FF3B6B]'}`}>{inv.status}</span>
                      <span className="text-[#6B6B7A]"><Users className="w-3 h-3 inline mr-1" />{inv.recipients_count || 0}</span>
                      <span className="text-[#6B6B7A]"><Mail className="w-3 h-3 inline mr-1" />{inv.sent_count || 0} sent</span>
                      <span className="text-[#06D6A0]"><CheckCircle2 className="w-3 h-3 inline mr-1" />{inv.yes_count || 0} yes</span>
                    </div>
                  </Link>
                  <div className="flex flex-col gap-1">
                    <Link to={`/my-invites/${inv.invitation_id}`} className="p-2 rounded-full hover:bg-[#FFFDF8]" data-testid={`open-invite-${inv.invitation_id}`}>
                      <ChevronRight className="w-4 h-4 text-[#6B6B7A]" />
                    </Link>
                    <button onClick={() => remove(inv.invitation_id)} className="p-2 rounded-full hover:bg-[#FFE4EB]" data-testid={`delete-invite-${inv.invitation_id}`}>
                      <Trash2 className="w-4 h-4 text-[#FF3B6B]" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyInvites;
